import { useMemo, useState } from 'react'
import { AlertTriangle, Check, CheckCheck, Clock3, MailCheck, RefreshCw, Search, Send, X } from 'lucide-react'
import { StatusPill } from '../../components/StatusPill'
import { formatMoney, formatRelativeTime } from '../../lib/format'
import { useAppStore } from '../../store/MockAppStore'
import type { RequestStatus, SalonRequest } from '../../types'

const filters: Array<RequestStatus | 'all'> = ['all', 'pending', 'accepted', 'completed', 'unavailable']

export function RequestQueue() {
  const { requests, updateStatus, environmentMode } = useAppStore()
  const [filter, setFilter] = useState<RequestStatus | 'all'>('all')
  const [query, setQuery] = useState('')
  const visible = useMemo(() => requests
    .filter((request) => filter === 'all' || request.status === filter)
    .filter((request) => `${request.itemName} ${request.location} ${request.notes}`.toLowerCase().includes(query.toLowerCase()))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt)), [filter, query, requests])

  return (
    <div className="staff-view">
      <div className="staff-view__heading"><div><p className="eyebrow">Request queue</p><h1>Requests</h1><p>Newest requests appear first.</p></div><span className={`live-badge live-badge--${environmentMode}`}><i /> {environmentMode === 'live' ? 'Live requests' : environmentMode === 'prelaunch' ? 'Pre-launch data' : 'Local demo data'}</span></div>
      <div className="request-toolbar">
        <label className="search-field"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search requests" /></label>
        <div className="filter-tabs" role="group" aria-label="Filter by status">
          {filters.map((value) => <button className={filter === value ? 'active' : ''} key={value} onClick={() => setFilter(value)}>{value === 'all' ? 'All' : value}<span>{value === 'all' ? requests.length : requests.filter((request) => request.status === value).length}</span></button>)}
        </div>
      </div>
      <div className="request-list">
        {visible.map((request) => (
          <article className={`staff-request staff-request--${request.status}`} key={request.id}>
            <div className="staff-request__top"><div><span className="request-time"><Clock3 size={14} />{formatRelativeTime(request.createdAt)}</span><h2>{request.itemName}</h2></div><StatusPill status={request.status} /></div>
            <div className="request-meta"><span>{request.location}</span><span>{request.trackingCode}</span>{request.priceCents > 0 && <strong>{formatMoney(request.priceCents)} approval request</strong>}</div>
            {request.notes && <blockquote>“{request.notes}”</blockquote>}
            <DeliveryState state={request.notifications.email} />
            {(request.status === 'pending' || request.status === 'accepted') && (
              <div className="request-actions">
                {request.status === 'pending' && <button className="action-button action-button--accept" onClick={() => updateStatus(request.id, 'accepted')}><Check size={17} />Accept</button>}
                <button className="action-button action-button--complete" onClick={() => updateStatus(request.id, 'completed')}><CheckCheck size={17} />Complete</button>
                <button className="action-button action-button--decline" onClick={() => updateStatus(request.id, 'unavailable')}><X size={17} />Unavailable</button>
              </div>
            )}
          </article>
        ))}
        {visible.length === 0 && <div className="empty-state"><span>☾</span><h2>No requests here</h2><p>Try another filter or clear your search.</p></div>}
      </div>
    </div>
  )
}

function DeliveryState({ state }: { state: SalonRequest['notifications']['email'] }) {
  const presentation = {
    queued: { label: 'Email queued', detail: 'Waiting to send', icon: Send },
    processing: { label: 'Email sending', detail: 'Delivery in progress', icon: Send },
    retry: { label: 'Email retry scheduled', detail: 'A temporary delivery problem will be retried', icon: RefreshCw },
    sent: { label: 'Email delivered', detail: 'Resend accepted the notification', icon: MailCheck },
    failed: { label: 'Email delivery failed', detail: 'Review notification configuration before launch', icon: AlertTriangle },
  }[state]
  const DeliveryIcon = presentation.icon
  return <div className={`delivery-row delivery-row--${state}`} role="status" aria-label={presentation.label}><DeliveryIcon size={16} /><span><strong>{presentation.label}</strong><small>{presentation.detail}</small></span></div>
}
